import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import AdCreativeDesigner from './AdCreativeDesigner';
import type { 
  CampaignFormData, 
  CampaignType, 
  AdCreativeData,
  ImpressionLimits,
  ValidationResult
} from '../../types/campaign';

interface CampaignFormProps {
  initialData?: Partial<CampaignFormData>;
  onSubmit: (data: CampaignFormData) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  mode?: 'create' | 'edit';
}

const CampaignForm: React.FC<CampaignFormProps> = ({
  initialData = {},
  onSubmit,
  onCancel,
  loading = false,
  mode = 'create'
}) => {
  const [formData, setFormData] = useState<CampaignFormData>({
    name: '',
    description: '',
    campaignType: 'standard',
    status: 'draft',
    currency: 'USD',
    targetKeywords: [],
    negativeKeywords: [],
    negativeBrands: [],
    contentGroupTargeting: [],
    adCreatives: [],
    impressionLimits: {},
    ...initialData
  });

  const [validation, setValidation] = useState<ValidationResult>({
    isValid: true,
    errors: [],
    warnings: []
  });

  const [activeTab, setActiveTab] = useState<'basic' | 'targeting' | 'creatives' | 'limits'>('basic');

  // Update form data when initialData changes (for edit mode)
  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData(prev => ({
        ...prev,
        ...initialData,
        // Ensure arrays are properly initialized
        targetKeywords: initialData.targetKeywords || [],
        negativeKeywords: initialData.negativeKeywords || [],
        negativeBrands: initialData.negativeBrands || [],
        contentGroupTargeting: initialData.contentGroupTargeting || [],
        adCreatives: initialData.adCreatives || [],
        impressionLimits: initialData.impressionLimits || {}
      }));
    }
  }, [initialData]);

  // Validate form data
  useEffect(() => {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Basic validation
    if (!formData.name?.trim()) {
      errors.push('Kampanya adı gereklidir');
    }

    if (!formData.campaignType) {
      errors.push('Kampanya tipi seçilmelidir');
    }

    // Date validation
    if (formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (start >= end) {
        errors.push('Bitiş tarihi başlangıç tarihinden sonra olmalıdır');
      }
    }

    // Budget validation
    if (formData.budget && formData.budget < 10) {
      warnings.push('Düşük bütçeli kampanyalar sınırlı erişime sahip olabilir');
    }

    // Creative validation
    if (!formData.adCreatives || formData.adCreatives.length === 0) {
      warnings.push('En az bir reklam kreatifi eklemeniz önerilir');
    } else {
      formData.adCreatives.forEach((creative, index) => {
        if (!creative.name?.trim()) {
          errors.push(`Kreatif ${index + 1}: İsim gereklidir`);
        }
        if (!creative.displayType) {
          errors.push(`Kreatif ${index + 1}: Görüntü tipi seçilmelidir`);
        }
        if (creative.displayType === 'video_overlay' && !creative.videoUrl) {
          errors.push(`Kreatif ${index + 1}: Video overlay için video URL'si gereklidir`);
        }
        if (creative.displayType === 'third_party' && !creative.adServerTag) {
          errors.push(`Kreatif ${index + 1}: Üçüncü taraf reklam için ad server tag gereklidir`);
        }
      });
    }

    setValidation({
      isValid: errors.length === 0,
      errors,
      warnings
    });
  }, [formData]);

  const handleFieldChange = (field: keyof CampaignFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleKeywordAdd = (keyword: string) => {
    if (keyword.trim() && !formData.targetKeywords?.includes(keyword.trim())) {
      handleFieldChange('targetKeywords', [...(formData.targetKeywords || []), keyword.trim()]);
    }
  };

  const handleKeywordRemove = (index: number) => {
    const keywords = [...(formData.targetKeywords || [])];
    keywords.splice(index, 1);
    handleFieldChange('targetKeywords', keywords);
  };

  const handleNegativeKeywordAdd = (keyword: string) => {
    if (keyword.trim() && !formData.negativeKeywords?.includes(keyword.trim())) {
      handleFieldChange('negativeKeywords', [...(formData.negativeKeywords || []), keyword.trim()]);
    }
  };

  const handleNegativeKeywordRemove = (index: number) => {
    const keywords = [...(formData.negativeKeywords || [])];
    keywords.splice(index, 1);
    handleFieldChange('negativeKeywords', keywords);
  };

  // Content Group Targeting handlers
  const handleContentGroupAdd = (groupName: string, includeExclude: 'include' | 'exclude' = 'include') => {
    if (groupName.trim() && !formData.contentGroupTargeting?.some(g => g.groupName === groupName.trim())) {
      const newGroupTarget = {
        groupName: groupName.trim(),
        includeExclude,
        status: 'active' as const
      };
      handleFieldChange('contentGroupTargeting', [...(formData.contentGroupTargeting || []), newGroupTarget]);
    }
  };

  const handleContentGroupRemove = (index: number) => {
    const groups = [...(formData.contentGroupTargeting || [])];
    groups.splice(index, 1);
    handleFieldChange('contentGroupTargeting', groups);
  };

  const handleContentGroupToggle = (index: number) => {
    const groups = [...(formData.contentGroupTargeting || [])];
    groups[index].includeExclude = groups[index].includeExclude === 'include' ? 'exclude' : 'include';
    handleFieldChange('contentGroupTargeting', groups);
  };

  const handleCreativeAdd = () => {
    const newCreative: Partial<AdCreativeData> = {
      name: `Kreatif ${(formData.adCreatives?.length || 0) + 1}`,
      adType: 'banner',
      displayType: 'banner'
    };
    handleFieldChange('adCreatives', [...(formData.adCreatives || []), newCreative]);
  };

  const handleCreativeUpdate = (index: number, creative: Partial<AdCreativeData>) => {
    const creatives = [...(formData.adCreatives || [])];
    creatives[index] = creative as AdCreativeData;
    handleFieldChange('adCreatives', creatives);
  };

  const handleCreativeRemove = (index: number) => {
    const creatives = [...(formData.adCreatives || [])];
    creatives.splice(index, 1);
    handleFieldChange('adCreatives', creatives);
  };

  const handleImpressionLimitsChange = (limits: ImpressionLimits) => {
    handleFieldChange('impressionLimits', limits);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validation.isValid) return;

    try {
      // Format dates for backend (ISO 8601)
      const formattedData = {
        ...formData,
        startDate: formData.startDate ? new Date(formData.startDate).toISOString() : undefined,
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : undefined
      };
      
      await onSubmit(formattedData);
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const tabs = [
    { id: 'basic', name: 'Temel Bilgiler', icon: InformationCircleIcon },
    { id: 'targeting', name: 'Duygu Hedefleme', icon: InformationCircleIcon },
    { id: 'creatives', name: 'Kreatifler', icon: InformationCircleIcon },
    { id: 'limits', name: 'Limitler', icon: InformationCircleIcon }
  ];

  return (
    <div className="max-w-6xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Header */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-300">
                {mode === 'create' ? 'Yeni Kampanya Oluştur' : 'Kampanya Düzenle'}
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Reklam kampanyanızı yapılandırın ve yönetin
              </p>
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onCancel}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={!validation.isValid || loading}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Kaydediliyor...' : mode === 'create' ? 'Kampanya Oluştur' : 'Değişiklikleri Kaydet'}
              </button>
            </div>
          </div>

          {/* Validation Messages */}
          {(validation.errors.length > 0 || validation.warnings.length > 0) && (
            <div className="mt-4 space-y-2">
              {validation.errors.length > 0 && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">Hatalar</h3>
                      <ul className="mt-2 text-sm text-red-700 list-disc list-inside">
                        {validation.errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {validation.warnings.length > 0 && (
                <div className="rounded-md bg-yellow-50 p-4">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-yellow-800">Uyarılar</h3>
                      <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside">
                        {validation.warnings.map((warning, index) => (
                          <li key={index}>{warning}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="bg-white shadow rounded-lg">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                >
                  <tab.icon className="h-4 w-4 mr-2" />
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Basic Information Tab */}
            {activeTab === 'basic' && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                      Kampanya Adı *
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) => handleFieldChange('name', e.target.value)}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Kampanya adını girin"
                    />
                  </div>

                  <div>
                    <label htmlFor="campaignType" className="block text-sm font-medium text-gray-700 mb-1">
                      Kampanya Tipi *
                    </label>
                    <select
                      id="campaignType"
                      value={formData.campaignType}
                      onChange={(e) => handleFieldChange('campaignType', e.target.value as CampaignType)}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    >
                      <option value="standard">Standart</option>
                      <option value="premium">Premium</option>
                      <option value="sponsored">Sponsorlu</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
                    Açıklama
                  </label>
                  <textarea
                    id="description"
                    rows={3}
                    value={formData.description}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    placeholder="Kampanya açıklaması"
                  />
                </div>

                <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
                  <div>
                    <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
                      Başlangıç Tarihi
                    </label>
                    <input
                      type="datetime-local"
                      id="startDate"
                      value={formData.startDate}
                      onChange={(e) => handleFieldChange('startDate', e.target.value)}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
                      Bitiş Tarihi
                    </label>
                    <input
                      type="datetime-local"
                      id="endDate"
                      value={formData.endDate}
                      onChange={(e) => handleFieldChange('endDate', e.target.value)}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    />
                  </div>

                  <div>
                    <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-1">
                      Bütçe
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        id="budget"
                        min="0"
                        step="0.01"
                        value={formData.budget || ''}
                        onChange={(e) => handleFieldChange('budget', e.target.value ? parseFloat(e.target.value) : undefined)}
                        className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm pr-12"
                        placeholder="0.00"
                      />
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">{formData.currency}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Targeting Tab */}
            {activeTab === 'targeting' && (
              <div className="space-y-6">
                {/* Target Keywords */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Hedef Kelimeler
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    Bu kelimeler içeren içeriklerde reklamınız gösterilecek. Virgülle ayırarak birden fazla kelime ekleyebilirsiniz.
                  </p>
                  <div className="space-y-2">
                    <div className="flex">
                      <input
                        type="text"
                        placeholder="Kelime ekle (virgülle ayırın) ve Enter'a bas"
                        className="flex-1 border-gray-300 rounded-l-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const value = e.currentTarget.value.trim();
                            if (value.includes(',')) {
                              // Virgülle ayrılmış kelimeleri toplu ekle
                              const keywords = value.split(',').map(k => k.trim()).filter(k => k);
                              keywords.forEach(keyword => handleKeywordAdd(keyword));
                            } else if (value) {
                              handleKeywordAdd(value);
                            }
                            e.currentTarget.value = '';
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                          const value = input.value.trim();
                          if (value.includes(',')) {
                            // Virgülle ayrılmış kelimeleri toplu ekle
                            const keywords = value.split(',').map(k => k.trim()).filter(k => k);
                            keywords.forEach(keyword => handleKeywordAdd(keyword));
                          } else if (value) {
                            handleKeywordAdd(value);
                          }
                          input.value = '';
                        }}
                        className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-500 text-sm"
                      >
                        <PlusIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.targetKeywords?.map((keyword, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 group"
                        >
                          <span
                            contentEditable
                            suppressContentEditableWarning={true}
                            className="outline-none bg-transparent"
                            onBlur={(e) => {
                              const newValue = e.currentTarget.textContent?.trim() || '';
                              if (newValue && newValue !== keyword) {
                                const newKeywords = [...(formData.targetKeywords || [])];
                                newKeywords[index] = newValue;
                                handleFieldChange('targetKeywords', newKeywords);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                e.currentTarget.blur();
                              }
                            }}
                          >
                            {keyword}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleKeywordRemove(index)}
                            className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    {formData.targetKeywords && formData.targetKeywords.length > 0 && (
                      <div className="text-xs text-gray-500">
                        Toplam {formData.targetKeywords.length} kelime. Kelimelerin üzerine tıklayarak düzenleyebilirsiniz.
                      </div>
                    )}
                  </div>
                </div>

                {/* Negative Keywords */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Negatif Kelimeler
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    Bu kelimeler içeren içeriklerde reklamınız gösterilmeyecek. Virgülle ayırarak birden fazla kelime ekleyebilirsiniz.
                  </p>
                  <div className="space-y-2">
                    <div className="flex">
                      <input
                        type="text"
                        placeholder="Negatif kelime ekle (virgülle ayırın) ve Enter'a bas"
                        className="flex-1 border-gray-300 rounded-l-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            const value = e.currentTarget.value.trim();
                            if (value.includes(',')) {
                              // Virgülle ayrılmış kelimeleri toplu ekle
                              const keywords = value.split(',').map(k => k.trim()).filter(k => k);
                              keywords.forEach(keyword => handleNegativeKeywordAdd(keyword));
                            } else if (value) {
                              handleNegativeKeywordAdd(value);
                            }
                            e.currentTarget.value = '';
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                          const value = input.value.trim();
                          if (value.includes(',')) {
                            // Virgülle ayrılmış kelimeleri toplu ekle
                            const keywords = value.split(',').map(k => k.trim()).filter(k => k);
                            keywords.forEach(keyword => handleNegativeKeywordAdd(keyword));
                          } else if (value) {
                            handleNegativeKeywordAdd(value);
                          }
                          input.value = '';
                        }}
                        className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-500 text-sm"
                      >
                        <PlusIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {formData.negativeKeywords?.map((keyword, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 group"
                        >
                          <span
                            contentEditable
                            suppressContentEditableWarning={true}
                            className="outline-none bg-transparent"
                            onBlur={(e) => {
                              const newValue = e.currentTarget.textContent?.trim() || '';
                              if (newValue && newValue !== keyword) {
                                const newKeywords = [...(formData.negativeKeywords || [])];
                                newKeywords[index] = newValue;
                                handleFieldChange('negativeKeywords', newKeywords);
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                e.currentTarget.blur();
                              }
                            }}
                          >
                            {keyword}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleNegativeKeywordRemove(index)}
                            className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full text-red-400 hover:bg-red-200 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    {formData.negativeKeywords && formData.negativeKeywords.length > 0 && (
                      <div className="text-xs text-gray-500">
                        Toplam {formData.negativeKeywords.length} negatif kelime. Kelimelerin üzerine tıklayarak düzenleyebilirsiniz.
                      </div>
                    )}
                  </div>
                </div>

                {/* Negative Brands */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Negatif Markalar
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    Bu markalar içeren içeriklerde reklamınız gösterilmeyecek
                  </p>
                  {/* Add negative brand form here */}
                </div>

                {/* Content Group Targeting */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    İçerik Grup Hedeflemesi
                  </label>
                  <p className="text-sm text-gray-500 mb-2">
                    Belirli grup isimlerine sahip dizileri dahil edin veya hariç tutun
                  </p>
                  <div className="space-y-2">
                    <div className="flex">
                      <input
                        type="text"
                        placeholder="Grup ismi girin (örn: Breaking Bad Sezon 1)"
                        className="flex-1 border-gray-300 rounded-l-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleContentGroupAdd(e.currentTarget.value);
                            e.currentTarget.value = '';
                          }
                        }}
                      />
                      <button
                        type="button"
                        onClick={(e) => {
                          const input = e.currentTarget.previousElementSibling as HTMLInputElement;
                          handleContentGroupAdd(input.value);
                          input.value = '';
                        }}
                        className="inline-flex items-center px-3 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 text-gray-500 text-sm"
                      >
                        <PlusIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="space-y-2">
                      {formData.contentGroupTargeting?.map((group, index) => (
                        <div
                          key={index}
                          className="inline-flex items-center justify-between w-full px-3 py-2 text-sm bg-gray-50 rounded-md"
                        >
                          <span className="flex-1">{group.groupName}</span>
                          <div className="flex items-center space-x-2">
                            <button
                              type="button"
                              onClick={() => handleContentGroupToggle(index)}
                              className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                                group.includeExclude === 'include'
                                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                  : 'bg-red-100 text-red-800 hover:bg-red-200'
                              }`}
                            >
                              {group.includeExclude === 'include' ? '✓ Dahil Et' : '✗ Hariç Tut'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleContentGroupRemove(index)}
                              className="inline-flex items-center justify-center w-6 h-6 rounded-full text-gray-400 hover:bg-gray-200 hover:text-gray-600"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Creatives Tab */}
            {activeTab === 'creatives' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Reklam Kreatifler</h3>
                  <button
                    type="button"
                    onClick={handleCreativeAdd}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Kreatif Ekle
                  </button>
                </div>

                {formData.adCreatives && formData.adCreatives.length > 0 ? (
                  <div className="space-y-6">
                    {formData.adCreatives.map((creative, index) => (
                      <AdCreativeDesigner
                        key={index}
                        creative={creative}
                        onChange={(updatedCreative) => handleCreativeUpdate(index, updatedCreative)}
                        onRemove={() => handleCreativeRemove(index)}
                        showRemove={formData.adCreatives!.length > 1}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-gray-50 rounded-lg">
                    <p className="text-gray-500">Henüz kreatif eklenmedi</p>
                    <button
                      type="button"
                      onClick={handleCreativeAdd}
                      className="mt-2 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-600 bg-blue-100 hover:bg-blue-200"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      İlk Kreatifi Ekle
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Limits Tab */}
            {activeTab === 'limits' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Gösterim Limitleri</h3>
                
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="dailyCampaign" className="block text-sm font-medium text-gray-700 mb-1">
                      Günlük Kampanya Limiti
                    </label>
                    <input
                      type="number"
                      id="dailyCampaign"
                      min="0"
                      value={formData.impressionLimits?.dailyCampaign || ''}
                      onChange={(e) => handleImpressionLimitsChange({
                        ...formData.impressionLimits,
                        dailyCampaign: e.target.value ? parseInt(e.target.value) : undefined
                      })}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Sınırsız"
                    />
                  </div>

                  <div>
                    <label htmlFor="totalCampaign" className="block text-sm font-medium text-gray-700 mb-1">
                      Toplam Kampanya Limiti
                    </label>
                    <input
                      type="number"
                      id="totalCampaign"
                      min="0"
                      value={formData.impressionLimits?.totalCampaign || ''}
                      onChange={(e) => handleImpressionLimitsChange({
                        ...formData.impressionLimits,
                        totalCampaign: e.target.value ? parseInt(e.target.value) : undefined
                      })}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Sınırsız"
                    />
                  </div>

                  <div>
                    <label htmlFor="dailyUser" className="block text-sm font-medium text-gray-700 mb-1">
                      Kullanıcı Başına Günlük Limit
                    </label>
                    <input
                      type="number"
                      id="dailyUser"
                      min="0"
                      value={formData.impressionLimits?.dailyUser || ''}
                      onChange={(e) => handleImpressionLimitsChange({
                        ...formData.impressionLimits,
                        dailyUser: e.target.value ? parseInt(e.target.value) : undefined
                      })}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Sınırsız"
                    />
                  </div>

                  <div>
                    <label htmlFor="weeklyUser" className="block text-sm font-medium text-gray-700 mb-1">
                      Kullanıcı Başına Haftalık Limit
                    </label>
                    <input
                      type="number"
                      id="weeklyUser"
                      min="0"
                      value={formData.impressionLimits?.weeklyUser || ''}
                      onChange={(e) => handleImpressionLimitsChange({
                        ...formData.impressionLimits,
                        weeklyUser: e.target.value ? parseInt(e.target.value) : undefined
                      })}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      placeholder="Sınırsız"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};

export default CampaignForm;